import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';

import { api } from '../lib/api.js';
import { useFiltros } from '../lib/filtros.jsx';
import { useMask } from '../piezas/Sesion.jsx';
import { Pagehead } from '../piezas/Cascaron.jsx';
import { Cargando, Error as AvisoError, Vacio } from '../piezas/Estados.jsx';
import {
  MapaMx,
  acomodar,
  mxX,
  mxY,
  type EtiquetaMapa,
  type GuiaMapa,
  type PuntoMapa,
} from '../piezas/MapaMx.jsx';

/**
 * Consulta pivote: cualquier dimension contra cualquier otra, con una medida.
 *
 * La rejilla se arma aqui y la suma la hace la base. Lo que llega del
 * servidor son los CRUCES que existen, no una matriz: una matriz con muchas
 * columnas vacias pesa mas que sus propios datos.
 */

interface Opcion {
  clave: string;
  etiqueta: string;
  dinero?: boolean;
}

interface Celda {
  fila: string;
  columna: string;
  valor: number;
}

interface CiudadMapa {
  clave: string;
  ciudad: string;
  latitud: number | null;
  longitud: number | null;
  servicios: number;
  terminados: number;
  con_incidencia: number;
}

interface Respuesta {
  fila: Opcion;
  columna: Opcion;
  medida: Opcion;
  meses: number;
  fuente_vacia: boolean;
  fuente: string | null;
  eventos: number;
  celdas: Celda[];
  por_ciudad: CiudadMapa[];
}

const MESES = [6, 12, 24, 36];

export function Consulta(): JSX.Element {
  const { consulta: filtrosQuery } = useFiltros();
  const mask = useMask();

  const [opciones, setOpciones] = useState<{ dimensiones: Opcion[]; medidas: Opcion[] } | null>(
    null,
  );
  const [fila, setFila] = useState('cliente');
  const [columna, setColumna] = useState('mes');
  const [medida, setMedida] = useState('tickets');
  const [meses, setMeses] = useState(6);
  const [datos, setDatos] = useState<Respuesta | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api
      .obtener<{ dimensiones: Opcion[]; medidas: Opcion[] }>('/v1/consulta/opciones')
      .then(({ datos: d }) => setOpciones(d))
      .catch(setError);
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(filtrosQuery.replace(/^\?/, ''));
    p.set('fila', fila);
    p.set('columna', columna);
    p.set('medida', medida);
    p.set('meses', String(meses));
    setDatos(null);
    setError(null);
    api
      .obtener<Respuesta>(`/v1/consulta?${p.toString()}`)
      .then(({ datos: d }) => setDatos(d))
      .catch(setError);
  }, [fila, columna, medida, meses, filtrosQuery]);

  /* La rejilla: filas y columnas ordenadas, y el maximo para el mapa de
     calor. Se calcula una vez por respuesta y no en cada celda. */
  const rejilla = useMemo(() => {
    if (!datos) return null;
    const filas = [...new Set(datos.celdas.map((c) => c.fila))].sort((a, b) =>
      a.localeCompare(b, 'es'),
    );
    const columnas = [...new Set(datos.celdas.map((c) => c.columna))].sort((a, b) =>
      a.localeCompare(b, 'es'),
    );
    /* Mapa ANIDADO y no una llave compuesta: cualquier separador que se
       elija puede aparecer dentro de un nombre de cliente o de ciudad, y ese
       dia dos cruces distintos se pisarian en silencio. */
    const mapa = new Map<string, Map<string, number>>();
    for (const c of datos.celdas) {
      let porColumna = mapa.get(c.fila);
      if (!porColumna) {
        porColumna = new Map<string, number>();
        mapa.set(c.fila, porColumna);
      }
      porColumna.set(c.columna, c.valor);
    }
    const maximo = Math.max(0, ...datos.celdas.map((c) => Math.abs(c.valor)));
    const totalFila = new Map<string, number>();
    const totalColumna = new Map<string, number>();
    let total = 0;
    for (const c of datos.celdas) {
      totalFila.set(c.fila, (totalFila.get(c.fila) ?? 0) + c.valor);
      totalColumna.set(c.columna, (totalColumna.get(c.columna) ?? 0) + c.valor);
      total += c.valor;
    }
    return { filas, columnas, mapa, maximo, totalFila, totalColumna, total };
  }, [datos]);

  /**
   * El mapa: tres marcadores por ciudad —total en azul, terminados en verde,
   * incidencias en rojo— sobre la silueta de la republica.
   *
   * Se arma aqui y no en el servidor porque lo unico que falta es geometria:
   * el servidor ya mando los conteos y las coordenadas del catalogo.
   */
  const mapa = useMemo(() => {
    const ciudades = datos?.por_ciudad ?? [];
    const conCoord = ciudades.filter(
      (c): c is CiudadMapa & { latitud: number; longitud: number } =>
        c.latitud !== null && c.longitud !== null,
    );
    const sinCoord = ciudades.filter((c) => c.latitud === null || c.longitud === null);

    const puntos: PuntoMapa[] = [];
    const etiquetas: EtiquetaMapa[] = [];
    const guias: GuiaMapa[] = [];
    const desplaza = acomodar();

    /* Solo las de mayor operacion llevan el grupo de tres cifras y su nombre.
     *
     * El diseno de tres marcadores por ciudad viene del prototipo, que tenia
     * ocho ciudades. Con las setenta y cuatro que hay en cuanto el catalogo
     * trae coordenadas son doscientos veintidos circulos sobre el mismo
     * centro del pais: se tapan unos a otros, los nombres se encabalgan y el
     * mapa deja de decir nada. Dibujarlo todo no es mostrarlo todo.
     *
     * Asi que las de mas peso se dibujan completas y el resto queda como un
     * punto, que sigue diciendo "aqui hay operacion" y conserva su detalle al
     * posar el puntero. La tabla de abajo las lleva todas, sin recortar. */
    const DESTACADAS = 12;
    const porVolumen = [...conCoord].sort((a, b) => b.servicios - a.servicios);
    const destacadas = new Set(porVolumen.slice(0, DESTACADAS).map((c) => c.clave));

    for (const c of porVolumen.filter((c) => !destacadas.has(c.clave))) {
      /* Punto chico, y el radio crece con el volumen para que el mapa
         distinga una ciudad de treinta servicios de una de tres. */
      const r = 3 + Math.min(4, Math.round(Math.sqrt(c.servicios) / 2));
      puntos.push({
        lat: c.latitud,
        lng: c.longitud,
        clase: 'm-menor',
        valor: c.servicios,
        r,
        menor: true,
        nombre: `${c.ciudad} · ${c.servicios} servicios, ${c.terminados} terminados, ${c.con_incidencia} con incidencia`,
      });
    }

    /* De oeste a este: colocar en orden de longitud hace que el desplazamiento
       de un grupo solo dependa de sus vecinos ya puestos, y el resultado sea
       el mismo en cada dibujado. Ordenar por numero de servicios, en cambio,
       moveria los grupos cada vez que cambia un conteo. */
    for (const c of [...conCoord]
      .filter((c) => destacadas.has(c.clave))
      .sort((a, b) => mxX(a.longitud) - mxX(b.longitud))) {
      const dy = desplaza(mxX(c.longitud), mxY(c.latitud));
      if (dy) guias.push({ lat: c.latitud, lng: c.longitud, dy });
      const sufijo = dy ? ' (desplazado para que se lea; la linea marca su lugar real)' : '';
      const base = { lat: c.latitud, lng: c.longitud, dy, r: 15 } as const;
      puntos.push(
        {
          ...base,
          dx: -30,
          clase: 'm-tot',
          valor: c.servicios,
          nombre: `${c.ciudad} · total de servicios${sufijo}`,
        },
        {
          ...base,
          dx: 0,
          clase: 'm-ok',
          valor: c.terminados,
          nombre: `${c.ciudad} · terminados${sufijo}`,
        },
        {
          ...base,
          dx: 30,
          clase: 'm-bad',
          valor: c.con_incidencia,
          nombre: `${c.ciudad} · con incidencia${sufijo}`,
        },
      );
      etiquetas.push({ lat: c.latitud, lng: c.longitud, dx: 0, dy: dy - 24, nombre: c.ciudad });
    }

    const total = ciudades.reduce(
      (a, c) => ({
        servicios: a.servicios + c.servicios,
        terminados: a.terminados + c.terminados,
        incidencia: a.incidencia + c.con_incidencia,
      }),
      { servicios: 0, terminados: 0, incidencia: 0 },
    );

    return { ciudades, conCoord, sinCoord, puntos, etiquetas, guias, total, destacadas };
  }, [datos]);

  const formatear = (v: number): string => {
    if (!datos?.medida.dinero) return v.toLocaleString('es-MX');
    /* Los montos pasan por el mismo enmascarador que el resto de la
       plataforma: un perfil sin montos no deberia llegar aqui —la medida ni
       se le ofrece— pero si llegara, veria lo mismo que en todos lados. */
    return mask(v);
  };

  function descargarCsv(): void {
    if (!rejilla || !datos) return;
    const esc = (s: string): string => `"${s.replace(/"/g, '""')}"`;
    const lineas = [
      [
        esc(`${datos.fila.etiqueta} / ${datos.columna.etiqueta}`),
        ...rejilla.columnas.map(esc),
      ].join(','),
      ...rejilla.filas.map((f) =>
        [
          esc(f),
          ...rejilla.columnas.map((c) => {
            const v = rejilla.mapa.get(f)?.get(c) ?? 0;
            /* En el CSV los montos van en PESOS con dos decimales, no en
               centavos: el archivo se abre en una hoja de calculo y ahi un
               entero de centavos se suma mal sin que nadie lo note. */
            return datos.medida.dinero ? (v / 100).toFixed(2) : String(v);
          }),
        ].join(','),
      ),
    ];
    /* La marca de orden de bytes va a proposito: sin ella Excel abre el CSV
       como latin-1 y "Monterrey, Nuevo Leon" llega con la n partida. Se arma
       con fromCharCode y no escrita en el fuente, donde seria un caracter
       invisible que el linter marca con razon. */
    const bom = String.fromCharCode(0xfe_ff);
    const blob = new Blob([bom + lineas.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `consulta_${fila}_${columna}_${medida}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (error !== null)
    return (
      <>
        <Pagehead />
        <AvisoError error={error} />
      </>
    );

  return (
    <>
      <Pagehead />
      <div className="card">
        <div className="toolbar" data-prueba="controles-pivote">
          <label className="sel">
            <span>Filas</span>
            <select
              value={fila}
              onChange={(e) => setFila(e.target.value)}
              data-prueba="pivote-fila"
            >
              {(opciones?.dimensiones ?? []).map((d) => (
                <option key={d.clave} value={d.clave} disabled={d.clave === columna}>
                  {d.etiqueta}
                </option>
              ))}
            </select>
          </label>

          <label className="sel">
            <span>Columnas</span>
            <select
              value={columna}
              onChange={(e) => setColumna(e.target.value)}
              data-prueba="pivote-columna"
            >
              {(opciones?.dimensiones ?? []).map((d) => (
                /* La misma dimension en los dos ejes da una diagonal y nada
                   mas. Se deshabilita en vez de dejar elegirla y contestar
                   con un error: el control dice lo que se puede hacer. */
                <option key={d.clave} value={d.clave} disabled={d.clave === fila}>
                  {d.etiqueta}
                </option>
              ))}
            </select>
          </label>

          <label className="sel">
            <span>Medida</span>
            <select
              value={medida}
              onChange={(e) => setMedida(e.target.value)}
              data-prueba="pivote-medida"
            >
              {(opciones?.medidas ?? []).map((m) => (
                <option key={m.clave} value={m.clave}>
                  {m.etiqueta}
                </option>
              ))}
            </select>
          </label>

          <label className="sel">
            <span>Periodo</span>
            <select
              value={meses}
              onChange={(e) => setMeses(Number(e.target.value))}
              data-prueba="pivote-meses"
            >
              {MESES.map((m) => (
                <option key={m} value={m}>
                  {m} meses
                </option>
              ))}
            </select>
          </label>

          {datos && (
            <span className="chip-dato info" data-prueba="pivote-eventos">
              {datos.eventos.toLocaleString('es-MX')} servicios en el periodo
            </span>
          )}

          <span className="sp" />
          <button
            type="button"
            className="btn ghost"
            onClick={descargarCsv}
            disabled={!rejilla || rejilla.filas.length === 0}
            data-prueba="pivote-csv"
          >
            Exportar CSV
          </button>
        </div>

        {/* La lista de medidas la filtra el servidor: si no llego ninguna
            economica, este perfil no recibe montos. Se dice aqui, donde se
            notaria la ausencia, en vez de dejar una lista mas corta sin
            explicacion. */}
        {opciones !== null && !opciones.medidas.some((m) => m.dinero) && (
          <div className="hint" data-prueba="pivote-sin-montos">
            Tu perfil no recibe informacion economica: las medidas de ingreso, costo y margen no
            aparecen en la lista.
          </div>
        )}

        {!datos || !rejilla ? (
          <Cargando />
        ) : rejilla.filas.length === 0 ? (
          /* Dos vacios distintos, dichos distinto. Una rejilla en blanco los
             cuenta igual, y asi es como una medida sin datos cargados pasa
             por una falla del sistema. */
          <Vacio
            mensaje={
              datos.fuente_vacia
                ? `Esta medida sale de "${datos.fuente ?? ''}", y esa tabla no tiene ` +
                  'ningun registro todavia. No es un filtro que no encontro nada: ' +
                  'es que el dato aun no se captura en la plataforma.'
                : 'Ningun ticket cae en este cruce con los filtros y el periodo elegidos.'
            }
          />
        ) : (
          <div className="pivote" data-prueba="tabla-pivote">
            <table>
              <thead>
                <tr>
                  <th scope="col">{datos.fila.etiqueta}</th>
                  {rejilla.columnas.map((c) => (
                    <th key={c} scope="col" className="num">
                      {c}
                    </th>
                  ))}
                  <th scope="col" className="num">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {rejilla.filas.map((f) => (
                  <tr key={f}>
                    <th scope="row">{f}</th>
                    {rejilla.columnas.map((c) => {
                      const v = rejilla.mapa.get(f)?.get(c) ?? 0;
                      const p = rejilla.maximo ? Math.abs(v) / rejilla.maximo : 0;
                      return (
                        <td key={c} className="num">
                          {/* El calor es un fondo, NUNCA el unico portador del
                              dato: el numero esta escrito en la celda. Quien
                              no distingue el tono lee el numero igual. */}
                          <span
                            className="calor"
                            style={{
                              background: `color-mix(in srgb, var(--brand) ${Math.round(p * 55)}%, transparent)`,
                            }}
                          >
                            {formatear(v)}
                          </span>
                        </td>
                      );
                    })}
                    <td className="num">
                      <b>{formatear(rejilla.totalFila.get(f) ?? 0)}</b>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">Total</th>
                  {rejilla.columnas.map((c) => (
                    <td key={c} className="num">
                      <b>{formatear(rejilla.totalColumna.get(c) ?? 0)}</b>
                    </td>
                  ))}
                  <td className="num">
                    <b>{formatear(rejilla.total)}</b>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* -----------------------------------------------------------------
          Operacion en la republica.

          Acompana a la rejilla porque contesta la pregunta que la rejilla no
          puede: DONDE pasa lo que se esta contando. La tabla cruza dos
          dimensiones cualesquiera, y en la mayoria de los cruces la ciudad ni
          sale; el mapa la pone siempre, con los mismos filtros y el mismo
          periodo.
          ----------------------------------------------------------------- */}
      {datos && mapa.ciudades.length > 0 && (
        <div className="card" style={{ marginTop: '14px' }} data-prueba="mapa-operacion">
          <div className="toolbar">
            <h2 style={{ margin: 0 }}>Operacion en la republica</h2>
            <span className="chip-dato info">
              {mapa.total.servicios.toLocaleString('es-MX')} servicios
            </span>
            <span className="chip-dato ok">
              {mapa.total.terminados.toLocaleString('es-MX')} terminados
            </span>
            <span className="chip-dato bad">
              {mapa.total.incidencia.toLocaleString('es-MX')} con incidencia
            </span>
            <span className="sp" />
            <span className="mini">
              {mapa.conCoord.length.toLocaleString('es-MX')} ciudad(es) en el mapa
              {mapa.conCoord.length > mapa.destacadas.size
                ? ` · las ${mapa.destacadas.size} de mayor operacion con cifras, el resto como punto`
                : ''}{' '}
              · respeta el filtro global
            </span>
          </div>

          {mapa.conCoord.length > 0 ? (
            <MapaMx puntos={mapa.puntos} etiquetas={mapa.etiquetas} guias={mapa.guias} alto={460} />
          ) : (
            <Vacio mensaje="Ninguna ciudad con operacion tiene coordenadas capturadas. Se capturan en Catalogos · Ciudades." />
          )}

          {/* El color NO es el unico portador: cada marcador lleva su numero
              escrito dentro, y la leyenda nombra lo que significa cada uno. */}
          <div className="mapa-leyenda">
            <span>
              <b style={{ background: '#2563eb' }} />
              Total de servicios
            </span>
            <span>
              <b style={{ background: '#16a34a' }} />
              Terminados
            </span>
            <span>
              <b style={{ background: '#dc2626' }} />
              Con incidencia: visita en falso o cierre administrativo
            </span>
            {mapa.conCoord.length > mapa.destacadas.size && (
              <span>
                <b style={{ background: 'var(--txt-3-ac)', width: '8px', height: '8px' }} />
                Otras ciudades con operacion · posa el puntero para ver sus cifras
              </span>
            )}
          </div>

          {/* Una ciudad sin coordenadas no se esfuma del mapa en silencio: se
              nombra y se dice donde se arregla. Desaparecer sin avisar es como
              un numero que no cuadra y nadie sabe por que. */}
          {mapa.sinCoord.length > 0 && (
            <div
              className="warnbox"
              style={{ marginTop: '10px' }}
              data-prueba="mapa-sin-coordenadas"
            >
              <b>
                {mapa.sinCoord.length.toLocaleString('es-MX')} ciudad(es) con operacion quedan fuera
                del mapa
              </b>{' '}
              por no tener coordenadas.{' '}
              {/* Nombrarlas TODAS era la idea del prototipo, que suponia un
                  punado. Con 66 ciudades el aviso se vuelve un parrafo que
                  nadie lee, y el aviso deja de avisar. Se nombran las de mas
                  operacion —las que mas pesa no ver en el mapa— y el resto se
                  cuenta; la tabla de abajo las lleva todas, marcadas. */}
              Las de mayor operacion son{' '}
              {mapa.sinCoord
                .slice(0, 6)
                .map((c) => c.ciudad)
                .join(', ')}
              {mapa.sinCoord.length > 6
                ? ` y ${(mapa.sinCoord.length - 6).toLocaleString('es-MX')} mas`
                : ''}
              . Estan listadas abajo con su marca, y las coordenadas se capturan en Catalogos ·
              Ciudades.
            </div>
          )}

          <div className="tabla-mapa">
            <table data-prueba="tabla-mapa-ciudades">
              <thead>
                <tr>
                  <th scope="col">Ciudad</th>
                  <th scope="col" className="num">
                    Total de servicios
                  </th>
                  <th scope="col" className="num">
                    Terminados
                  </th>
                  <th scope="col" className="num">
                    Con incidencia
                  </th>
                  <th scope="col" className="num">
                    % terminados
                  </th>
                </tr>
              </thead>
              <tbody>
                {mapa.ciudades.map((c) => {
                  const pct = c.servicios ? Math.round((c.terminados / c.servicios) * 100) : 0;
                  const mal = c.con_incidencia / Math.max(1, c.servicios) > 0.15;
                  return (
                    <tr key={c.clave}>
                      <th scope="row">
                        {c.ciudad}
                        {c.latitud === null || c.longitud === null ? (
                          <span className="chip-dato warn"> sin coordenadas</span>
                        ) : null}
                      </th>
                      <td className="num">{c.servicios.toLocaleString('es-MX')}</td>
                      <td className="num">{c.terminados.toLocaleString('es-MX')}</td>
                      <td className="num">
                        {c.con_incidencia ? (
                          <span className="chip-dato bad">
                            {c.con_incidencia.toLocaleString('es-MX')}
                          </span>
                        ) : (
                          '0'
                        )}
                      </td>
                      <td className="num">
                        <span className="pct-barra">
                          <span className={mal ? 'bar warn' : 'bar'}>
                            <i style={{ width: `${pct}%` }} />
                          </span>
                          <span className="mini">{pct}%</span>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="mini" style={{ margin: '8px 0 0' }}>
            El mapa es un esquema dibujado en la propia pantalla, sin red: ubica la operacion, no
            mide distancias. Las coordenadas salen del catalogo de ciudades.
          </p>
        </div>
      )}
    </>
  );
}
